import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import {
  createReward,
  deactivateReward,
  fetchRewards,
  updateReward,
  uploadRewardImage,
} from '../services/rewardStoreApi';
import Alert from './Alert';
import Confirm from './Confirm';
import PermissionGate from './PermissionGate';

const categories = ['Beverages', 'Desserts', 'Food', 'Merch','Friendship Day'];

function normalizeForm(reward) {
  return {
    title: reward?.title || '',
    description: reward?.description || '',
    category: reward?.category || 'Beverages',
    tokenCost: reward?.tokenCost ? String(reward.tokenCost) : '',
    ristaDiscountCode: reward?.ristaDiscountCode || '',
    status: reward?.status || 'active',
  };
}

function RewardForm({ currentReward, saving, onCancel, onSubmit }) {
  const [form, setForm] = useState(() => normalizeForm(currentReward));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(currentReward?.imageUrl || '');

  useEffect(() => {
    setForm(normalizeForm(currentReward));
    setImageFile(null);
    setImagePreviewUrl(currentReward?.imageUrl || '');
  }, [currentReward]);

  useEffect(() => {
    if (!imageFile) return undefined;

    const previewUrl = URL.createObjectURL(imageFile);
    setImagePreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [imageFile]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setImageFile(file);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      reward: {
        ...form,
        tokenCost: Number.parseInt(form.tokenCost, 10),
      },
      imageFile,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-lg w-full max-w-2xl max-h-svh overflow-y-auto">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-bold">{currentReward ? 'Edit Reward' : 'Add Reward'}</h3>
          <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-800">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block mb-2 font-medium">Reward Image</label>
            <div className="flex flex-col md:flex-row gap-4 md:items-center">
              <div className="w-32 h-32 bg-gray-100 rounded overflow-hidden flex items-center justify-center shrink-0 border">
                {imagePreviewUrl ? (
                  <img src={imagePreviewUrl} alt="Reward preview" className="w-full h-full object-cover" />
                ) : (
                  <PhotoIcon className="size-10 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  className="w-full border px-3 py-2 rounded"
                  onChange={handleImageChange}
                />
                <p className="text-sm text-gray-500 mt-2">
                  Upload a square product image. Images are saved when you save the reward.
                </p>
                {imageFile && (
                  <p className="text-sm text-teal-600 mt-1">{imageFile.name}</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium">Title</label>
            <input
              type="text"
              className="w-full border px-3 py-2 rounded"
              value={form.title}
              onChange={(event) => handleChange('title', event.target.value)}
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">Description</label>
            <textarea
              className="w-full border px-3 py-2 rounded min-h-24"
              value={form.description}
              onChange={(event) => handleChange('description', event.target.value)}
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium">Category</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={form.category}
                onChange={(event) => handleChange('category', event.target.value)}
                required
              >
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-1 font-medium">Token Cost</label>
              <input
                type="number"
                min="1"
                className="w-full border px-3 py-2 rounded"
                value={form.tokenCost}
                onChange={(event) => handleChange('tokenCost', event.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium">Rista Discount Code</label>
              <input
                type="text"
                className="w-full border px-3 py-2 rounded"
                value={form.ristaDiscountCode}
                onChange={(event) => handleChange('ristaDiscountCode', event.target.value)}
                required
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">Status</label>
              <select
                className="w-full border px-3 py-2 rounded"
                value={form.status}
                onChange={(event) => handleChange('status', event.target.value)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="border px-4 py-2 rounded hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-teal-500 text-white px-4 py-2 rounded disabled:opacity-60"
              disabled={saving}
            >
              {saving ? 'Saving...' : imageFile ? 'Save Reward & Image' : 'Save Reward'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RewardStore() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [currentReward, setCurrentReward] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imageUploadingId, setImageUploadingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const loadRewards = async () => {
    setLoading(true);
    try {
      const result = await fetchRewards();
      setRewards(result);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to load rewards.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRewards();
  }, []);

  const visibleRewards = useMemo(() => {
    return rewards.filter((reward) => {
      const statusMatch = statusFilter === 'all' || reward.status === statusFilter;
      const categoryMatch = categoryFilter === 'all' || reward.category === categoryFilter;
      return statusMatch && categoryMatch;
    });
  }, [rewards, statusFilter, categoryFilter]);

  const startCreate = () => {
    setCurrentReward(null);
    setShowForm(true);
  };

  const startEdit = (reward) => {
    setCurrentReward(reward);
    setShowForm(true);
  };

  const handleSubmit = async ({ reward: payload, imageFile }) => {
    setSaving(true);
    try {
      let savedReward;
      if (currentReward) {
        savedReward = await updateReward(currentReward.id, payload);
      } else {
        savedReward = await createReward(payload);
      }

      if (imageFile) {
        await uploadRewardImage(savedReward.id, imageFile);
      }

      setAlertMessage(imageFile ? 'Reward and image saved successfully.' : 'Reward saved successfully.');
      setShowForm(false);
      setCurrentReward(null);
      await loadRewards();
    } catch (error) {
      setAlertMessage(error.message || 'Failed to save reward.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = (reward) => {
    setConfirmMessage(`Deactivate "${reward.title}"? It will no longer appear in the customer reward store.`);
    setConfirmCallback(() => async () => {
      try {
        await deactivateReward(reward.id);
        setConfirmMessage(null);
        setAlertMessage('Reward deactivated.');
        await loadRewards();
      } catch (error) {
        setAlertMessage(error.message || 'Failed to deactivate reward.');
      }
    });
  };

  const handleImageUpload = async (reward, file) => {
    if (!file) return;

    setImageUploadingId(reward.id);
    try {
      await uploadRewardImage(reward.id, file);
      setAlertMessage('Reward image uploaded.');
      await loadRewards();
    } catch (error) {
      setAlertMessage(error.message || 'Failed to upload reward image.');
    } finally {
      setImageUploadingId(null);
    }
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-xl">
      {alertMessage && (
        <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />
      )}
      {confirmMessage && (
        <Confirm
          message={confirmMessage}
          onConfirm={confirmCallback}
          onCancel={() => setConfirmMessage(null)}
        />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Store Rewards</h2>
          <p className="text-gray-500 mt-1">Create and manage rewards for the Got Tea loyalty store.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadRewards}
            className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50"
          >
            <ArrowPathIcon className="size-5" />
            Refresh
          </button>
          <PermissionGate permission="loyalty.rewards.create"><button
            type="button"
            onClick={startCreate}
            className="bg-teal-500 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <PlusIcon className="size-5" />
            Add Reward
          </button></PermissionGate>
        </div>
      </div>

      <div className="bg-white rounded shadow p-4 mb-5 flex flex-col md:flex-row gap-3">
        <select
          className="border px-3 py-2 rounded"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          className="border px-3 py-2 rounded"
          value={categoryFilter}
          onChange={(event) => setCategoryFilter(event.target.value)}
        >
          <option value="all">All categories</option>
          {categories.map((category) => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      {showForm && (
        <RewardForm
          currentReward={currentReward}
          saving={saving}
          onCancel={() => setShowForm(false)}
          onSubmit={handleSubmit}
        />
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className="mt-2">Loading rewards...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white shadow rounded text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Reward</th>
                <th className="py-2 px-4 border-b hidden md:table-cell">Category</th>
                <th className="py-2 px-4 border-b">Cost</th>
                <th className="py-2 px-4 border-b hidden lg:table-cell">Rista Code</th>
                <th className="py-2 px-4 border-b hidden md:table-cell">Status</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRewards.map((reward) => (
                <tr key={reward.id}>
                  <td className="py-3 px-4 border-b">
                    <div className="flex items-center gap-3 min-w-64">
                      <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex items-center justify-center shrink-0">
                        {reward.imageUrl ? (
                          <img src={reward.imageUrl} alt={reward.title} className="w-full h-full object-cover" />
                        ) : (
                          <PhotoIcon className="size-7 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-semibold">{reward.title}</div>
                        <div className="text-sm text-gray-500 line-clamp-2">{reward.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b hidden md:table-cell">{reward.category}</td>
                  <td className="py-3 px-4 border-b font-semibold text-teal-600">{reward.tokenCost}</td>
                  <td className="py-3 px-4 border-b hidden lg:table-cell">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">{reward.ristaDiscountCode}</code>
                  </td>
                  <td className="py-3 px-4 border-b hidden md:table-cell">
                    <span className={`px-2 py-1 rounded text-sm ${
                      reward.status === 'active' ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {reward.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 border-b">
                    <div className="flex items-center gap-2">
                      <PermissionGate permission="loyalty.rewards.upload"><label className="text-teal-500 hover:opacity-70 cursor-pointer">
                        <PhotoIcon className="size-7" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={imageUploadingId === reward.id}
                          onChange={(event) => {
                            handleImageUpload(reward, event.target.files[0]);
                            event.target.value = '';
                          }}
                        />
                      </label></PermissionGate>
                      <PermissionGate permission="loyalty.rewards.update"><button
                        type="button"
                        onClick={() => startEdit(reward)}
                        className="text-teal-500 hover:opacity-70"
                      >
                        <PencilSquareIcon className="size-7" />
                      </button></PermissionGate>
                      <PermissionGate permission="loyalty.rewards.delete"><button
                        type="button"
                        onClick={() => handleDeactivate(reward)}
                        className="text-red-500 hover:opacity-70"
                        disabled={reward.status !== 'active'}
                      >
                        <TrashIcon className="size-7" />
                      </button></PermissionGate>
                    </div>
                    {imageUploadingId === reward.id && (
                      <div className="text-xs text-gray-500 mt-1">Uploading...</div>
                    )}
                  </td>
                </tr>
              ))}
              {visibleRewards.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-8">
                    No rewards found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default RewardStore;
